import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  service,
  factories,
  models,
  IEmbedConfiguration,
  Report,
} from "powerbi-client";
import {
  getPowerBiReportId,
  GetUserDeptAndUnitDataForReport,
} from "../../Helpers/APIEndpoints/Endpoints";
import { useAuthStore } from "../../AuthStore/AuthStore";

// Initialize the Power BI Service
const powerbiService = new service.Service(
  factories.hpmFactory,
  factories.wpmpFactory,
  factories.routerFactory,
);

const DetailedRequestReport: React.FC = () => {
  const { instance, accounts } = useMsal();
  const reportRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { commonContextParams } = useAuthStore();

  const loadReport = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const userId = commonContextParams?.userId;
      const roleId = parseInt(commonContextParams?.roleId ?? "0", 10);

      if (!userId) throw new Error("User context (UserId) is missing.");

      // --- 1. Fetch User Context (Departments & Business Units) ---
      const userContextResponse = await fetch(
        `${GetUserDeptAndUnitDataForReport}?userId=${userId}&roleId=${roleId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      if (!userContextResponse.ok)
        throw new Error("Failed to fetch User Context.");
      const userRes = await userContextResponse.json();

      let deptValues: string[] = [];
      let buValues: string[] = [];

      // Extract names into arrays, filtering out any null/undefined entries
      if (userRes.status === 0 && userRes.data) {
        if (Array.isArray(userRes.data.departments)) {
          deptValues = userRes.data.departments
            .map((d: any) => d.name)
            .filter(
              (name: string) =>
                name !== null && name !== undefined && name !== "",
            );
        }
        if (Array.isArray(userRes.data.businessUnits)) {
          buValues = userRes.data.businessUnits
            .map((b: any) => b.name)
            .filter(
              (name: string) =>
                name !== null && name !== undefined && name !== "",
            );
        }
      }

      // --- 2. Fetch Report Config ---
      const reportConfigResponse = await fetch(
        `${getPowerBiReportId}?ReportName=DetailedRequestReport`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const reportResult = await reportConfigResponse.json();

      if (
        reportResult.status === 0 &&
        Array.isArray(reportResult.data) &&
        reportResult.data.length > 0
      ) {
        const reportEntry = reportResult.data[0];
        const configDetails = JSON.parse(reportEntry.ConfigValue);
        const { reportId, workspaceId } = configDetails;

        // --- 3. Token & Embed URL ---
        const tokenRequest = {
          account: accounts[0],
          scopes: ["https://analysis.windows.net/powerbi/api/Report.Read.All"],
        };
        const tokenResponse = await instance
          .acquireTokenSilent(tokenRequest)
          .catch(() => instance.acquireTokenPopup(tokenRequest));

        const accessToken = tokenResponse.accessToken;

        const pbiResponse = await fetch(
          `https://api.powerbi.com/v1.0/myorg/groups/${workspaceId}/reports/${reportId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        const pbiData = await pbiResponse.json();

        // --- 4. Build Conditional Filters ---
        const reportFilters: any[] = [];

        // Only add Department filter if values exist
        if (deptValues.length > 0) {
          reportFilters.push({
            $schema: "http://powerbi.com/product/schema#basic",
            target: { table: "Department", column: "Name" },
            operator: "In",
            values: deptValues,
            filterType: models.FilterType.BasicFilter,
          });
        }

        // Only add BusinessUnit filter if values exist
        if (buValues.length > 0) {
          reportFilters.push({
            $schema: "http://powerbi.com/product/schema#basic",
            target: { table: "BusinessUnit", column: "Name" },
            operator: "In",
            values: buValues,
            filterType: models.FilterType.BasicFilter,
          });
        }

        // --- 5. Embed ---
        if (reportRef.current) {
          powerbiService.reset(reportRef.current);

          const embedConfig: IEmbedConfiguration = {
            type: "report",
            id: pbiData.id,
            embedUrl: pbiData.embedUrl,
            accessToken: accessToken,
            tokenType: models.TokenType.Aad,
            settings: {
              panes: {
                filters: { visible: true, expanded: false },
                pageNavigation: { visible: true },
              },
              background: models.BackgroundType.Transparent,
            },
          };

          const report = powerbiService.embed(
            reportRef.current,
            embedConfig,
          ) as Report;

          report.off("loaded");
          report.on("loaded", async () => {
            try {
              // If reportFilters is empty (e.g. Super Admin), setFilters([]) clears any existing filters
              await report.setFilters(reportFilters);
              console.log(
                reportFilters.length > 0
                  ? "Applied Filters:"
                  : "No filters applied (Admin/Empty context)",
                reportFilters,
              );
            } catch (fErr) {
              console.error("PowerBI Filter Error:", fErr);
            }
          });
        }
      } else {
        throw new Error("Report configuration not found.");
      }
    } catch (err: any) {
      console.error("POWER BI ERROR:", err);
      setErrorMessage(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accounts.length > 0 && commonContextParams?.userId) {
      loadReport();
    }
  }, [accounts, instance, commonContextParams?.userId]);

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      {loading && (
        <p style={{ textAlign: "center", padding: "20px" }}>
          Loading Report Data...
        </p>
      )}
      {errorMessage && (
        <p style={{ color: "red", textAlign: "center" }}>{errorMessage}</p>
      )}
      <div
        ref={reportRef}
        style={{
          height: "100%",
          width: "100%",
          display: loading || errorMessage ? "none" : "block",
        }}
      />
    </div>
  );
};

export default DetailedRequestReport;
